/**
 * 生成枚数セレクタの選択肢を、組織の同時実行枠に合わせて絞る。
 *
 * BannerAdsPage はスタイル1件 = ジョブ1本にする。複数スタイルを選んだ場合の
 * 最悪ケースは 1枚 = 1ジョブなので、生成枚数を残りジョブ枠以内に制限する。
 * サーバ側のガード（ImageGenerationJobController の同時実行チェック）は
 * 枠を超えたジョブを 429 で弾くので、枠を超える枚数は最初から選ばせない。
 */

/** 枠に余裕がある時に選べる枚数。 */
export const IMAGE_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20] as const;

const ABSOLUTE_MAX = IMAGE_COUNT_OPTIONS[IMAGE_COUNT_OPTIONS.length - 1];

export interface ConcurrencyBudget {
  max_concurrent_jobs: number;
  active_jobs: number;
}

/**
 * いま選べる最大枚数。
 * 枠が未取得の間は選択を待つ。空き枠がゼロの場合も生成を受け付けない。
 */
export function maxSelectableImageCount(limits?: ConcurrencyBudget | null): number {
  if (!limits) return 0;

  const remaining = limits.max_concurrent_jobs - limits.active_jobs;

  return Math.max(0, Math.min(ABSOLUTE_MAX, remaining));
}

/** セレクタに出す選択肢。枠がない場合は空配列。 */
export function availableImageCountOptions(limits?: ConcurrencyBudget | null): number[] {
  const max = maxSelectableImageCount(limits);
  const options = IMAGE_COUNT_OPTIONS.filter((n) => n <= max);
  return options;
}

/** 現在の選択値が枠を超えていたら、選べる最大値まで引き下げる。 */
export function clampImageCount(current: number, limits?: ConcurrencyBudget | null): number {
  const options = availableImageCountOptions(limits);
  if (options.includes(current)) return current;
  return options[options.length - 1] ?? 0;
}

/**
 * 枠のせいで選択肢が絞られている時に出す説明。
 * 黙って選択肢が減ると「壊れている」と受け取られるので、理由と復帰条件を書く。
 */
export function concurrencyNotice(limits?: ConcurrencyBudget | null): string | null {
  if (!limits) return null;
  if (limits.active_jobs <= 0) return null;
  if (maxSelectableImageCount(limits) >= ABSOLUTE_MAX) return null;

  const remaining = Math.max(0, limits.max_concurrent_jobs - limits.active_jobs);

  if (remaining <= 0) {
    return `現在${limits.active_jobs}件を生成中です（同時に実行できるのは${limits.max_concurrent_jobs}件まで）。1件完了すると続けて生成できます。`;
  }

  return `現在${limits.active_jobs}件を生成中のため、あと${remaining}枚まで選べます（同時実行の上限は${limits.max_concurrent_jobs}件）。`;
}

/** カルーセルで一度に作れるパターン数の上限。 */
export const CAROUSEL_PATTERN_OPTIONS = [1, 2, 3, 4, 5] as const;

const CAROUSEL_ABSOLUTE_MAX = CAROUSEL_PATTERN_OPTIONS[CAROUSEL_PATTERN_OPTIONS.length - 1];

/**
 * カルーセルを何パターンまで同時に作れるか。
 * カルーセルは1スライド = 1ジョブなので、Nパターン × M枚 = N×M ジョブを消費する。
 *
 * 枠が1セットぶんに満たない時は 0 を返して生成そのものを受け付けない。
 * 1 を返して通すと、途中のスライドだけ 429 で欠けた「歯抜けのセット」ができる。
 * 枚数セレクタ側（maxSelectableImageCount）と同じく、枠が未取得の間も 0 で待つ。
 */
export function maxCarouselPatterns(
  limits: ConcurrencyBudget | null | undefined,
  slideCount: number
): number {
  if (!limits || slideCount <= 0) return 0;

  const remaining = limits.max_concurrent_jobs - limits.active_jobs;

  return Math.max(0, Math.min(CAROUSEL_ABSOLUTE_MAX, Math.floor(remaining / slideCount)));
}

/** カルーセルのパターン数セレクタに出す選択肢。枠が足りなければ空配列。 */
export function availableCarouselPatternOptions(
  limits: ConcurrencyBudget | null | undefined,
  slideCount: number
): number[] {
  const max = maxCarouselPatterns(limits, slideCount);
  return CAROUSEL_PATTERN_OPTIONS.filter((n) => n <= max);
}

/**
 * 1セットぶんの枠すら空いていない状態か（枠は取得済みだが足りない）。
 * 「なぜ生成できないのか」を利用者に説明するために使う。
 */
export function carouselMayBePartial(
  limits: ConcurrencyBudget | null | undefined,
  slideCount: number
): boolean {
  if (!limits || slideCount <= 0) return false;

  return limits.max_concurrent_jobs - limits.active_jobs < slideCount;
}
